import { useEffect, useMemo, useState } from "react";
import { usePrivy } from "@privy-io/react-auth";
import { authApi } from "../auth/api";
import { markReferralBound, pendingReferralCode } from "../lib/referral";
import { SOLANA_NETWORK } from "../contracts/solana/config";

export type AuthUser = {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
};

/**
 * Last-known session hint (the wallet address), persisted so a reload paints
 * the logged-in header immediately instead of flashing "Connect Wallet" while
 * Privy restores the session (`ready` is false for ~1-2s of network I/O).
 * Written once Privy resolves authenticated, cleared on logout / expiry — so
 * a genuinely logged-out visitor never sees a phantom logged-in state.
 */
const AUTH_HINT_KEY = "hupper:authHint";

function readAuthHint(): string | null {
  try {
    return localStorage.getItem(AUTH_HINT_KEY);
  } catch {
    return null;
  }
}

export function useAuth() {
  const { ready, authenticated, user, login, logout, getAccessToken } = usePrivy();
  // Read once on mount — the hint only bridges the pre-`ready` window.
  const [authHint] = useState<string | null>(() => readAuthHint());

  // Resolve the player's wallet from Privy's linked accounts. The app is
  // Solana-only (see PrivyProvider: walletChainType "solana-only" + Solana
  // embedded wallets), so this is the base58 address of the embedded or
  // connected Solana wallet. We read it from `linkedAccounts` — the same
  // source the server uses in verifyPrivyAccessToken — so the client address
  // always matches the wallet the server persists. NOTE: useWallets() from
  // "@privy-io/react-auth" only surfaces EVM wallets and returns nothing for a
  // Solana-only embedded wallet, which is why we don't use it here.
  const walletAddress = useMemo(() => {
    if (!authenticated || !user) return null;
    const wallets = user.linkedAccounts.filter(
      (a): a is typeof a & { address: string; chainType?: string } =>
        a.type === "wallet" && "address" in a,
    );
    // Prefer the wallet of the ACTIVE chain. Accounts that also carry a wallet
    // from the other chain (e.g. EVM-era embedded wallets) list it first, and
    // picking it here would poison everything downstream — the server session's
    // wallet claim, the vault reads and the quick-play sweep target.
    const preferred = wallets.find((a) =>
      SOLANA_NETWORK ? a.chainType === "solana" : a.chainType === "ethereum",
    );
    return preferred?.address ?? wallets[0]?.address ?? user.wallet?.address ?? null;
  }, [authenticated, user]);

  // Keep the hint in sync once Privy has resolved the real session state:
  // set while authenticated (with the resolved wallet), removed when the
  // session is really gone so the next reload goes straight to logged-out.
  useEffect(() => {
    if (!ready) return;
    try {
      if (authenticated && walletAddress) localStorage.setItem(AUTH_HINT_KEY, walletAddress);
      else if (!authenticated) localStorage.removeItem(AUTH_HINT_KEY);
    } catch {
      /* localStorage unavailable — hint is best-effort */
    }
  }, [ready, authenticated, walletAddress]);

  // Optimistic session restore: while Privy is still initialising, trust the
  // persisted hint so the header renders the logged-in cluster (avatar +
  // cached balance) instead of the Connect Wallet CTA on every reload. Reads
  // (balance RPC, avatar) start immediately with the hinted wallet, in
  // parallel with Privy's init. If the session actually expired, `ready`
  // lands with authenticated=false, the hint is cleared above and everything
  // falls back to logged-out. Writes are unaffected: apiToken only exists
  // after the real auth exchange below.
  const optimistic = !ready && !authenticated && authHint != null;

  const authUser = useMemo<AuthUser | null>(() => {
    if (!authenticated || !user) return null;
    const email = user.email?.address ?? user.google?.email ?? null;
    const displayName =
      user.google?.name ??
      (email ? email.split("@")[0] : null) ??
      (user.wallet?.address ? `${user.wallet.address.slice(0, 6)}…${user.wallet.address.slice(-4)}` : null);
    return {
      uid: user.id,
      email,
      displayName,
      photoURL: null,
    };
  }, [authenticated, user]);

  const [apiToken, setApiToken] = useState<string | null>(null);
  const [apiTokenError, setApiTokenError] = useState<string | null>(null);

  useEffect(() => {
    if (!authenticated) {
      setApiToken(null);
      setApiTokenError(null);
      return;
    }
    // The Solana embedded wallet is created asynchronously just AFTER `authenticated`
    // flips true; wait for it to land in linkedAccounts before the exchange so the
    // server provisions the user with a non-null wallet (re-fires when it resolves).
    if (!walletAddress) return;
    let cancelled = false;
    (async () => {
      try {
        const accessToken = await getAccessToken();
        if (!accessToken) throw new Error("no privy access token");
        // Forward the resolved display name (Google name / email local-part /
        // wallet abbrev) so the server-side users directory has something
        // searchable. Without this the friends panel only finds people by
        // wallet prefix.
        //
        // Also forward any pending referral code captured from `?ref=` on
        // landing. Server binds it permanently on first signup (no-op for
        // returning users, no-op for self-referrals, single-touch forever
        // after — handled by referralsStore.link).
        const refCode = pendingReferralCode();
        const res = await authApi.privy(
          accessToken,
          authUser?.displayName ?? null,
          refCode,
        );
        if (!cancelled) {
          setApiToken(res.token);
          setApiTokenError(null);
          if (refCode) markReferralBound();
        }
      } catch (err: any) {
        if (!cancelled) setApiTokenError(err?.message ?? "auth exchange failed");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authenticated, getAccessToken, authUser?.displayName, walletAddress]);

  return {
    ready,
    isLoggedIn: authenticated || optimistic,
    walletAddress: walletAddress ?? (optimistic ? authHint : null),
    authUser,
    firebaseUser: authUser,
    apiToken,
    apiTokenError,
    login,
    handleSignOut: logout,
    setShowAuthModal: (open: boolean) => {
      if (open) login();
    },
    showAuthModal: false,
  };
}
